import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ChevronLeft, ChevronRight, CalendarCheck } from 'lucide-react';
import { studentsApi } from '../../students.api';
import type { StudentAttendance } from '../profile.types';

interface Props {
  uuid: string;
  initial: StudentAttendance | null; // current-month snapshot already fetched
}

const STATUS_COLOR: Record<string, { bg: string; fg: string }> = {
  present: { bg: 'var(--success-soft)', fg: 'var(--success)' },
  absent:  { bg: 'var(--danger-soft)',  fg: 'var(--danger)' },
  late:    { bg: 'var(--warning-soft)', fg: 'var(--warning)' },
};

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(m: string, delta: number): string {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(m: string): string {
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

const AttendanceTab: React.FC<Props> = ({ uuid, initial }) => {
  const initMonth = initial?.month || currentMonth();
  const [month, setMonth] = useState(initMonth);
  const [data, setData] = useState<StudentAttendance | null>(initial);
  const [loading, setLoading] = useState(!initial);

  const load = useCallback((m: string) => {
    setLoading(true);
    studentsApi.attendance(uuid, m)
      .then((r) => setData(r.success ? r.data ?? null : null))
      .finally(() => setLoading(false));
  }, [uuid]);

  // Refetch when month changes away from the pre-loaded initial month.
  useEffect(() => {
    if (data && data.month === month) return;
    load(month);
  }, [month, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const att = data?.attendance;

  // Build day → status map
  const byDay: Record<number, string> = {};
  (data?.records ?? []).forEach((r) => {
    const day = Number(r.date.slice(8, 10));
    byDay[day] = r.status;
  });

  const [y, mo] = month.split('-').map(Number);
  const daysInMonth = new Date(y, mo, 0).getDate();
  const firstWeekday = new Date(y, mo - 1, 1).getDay(); // 0=Sun

  return (
    <div>
      {/* Month nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <button className="icon-btn" onClick={() => setMonth((m) => shiftMonth(m, -1))}><ChevronLeft size={18} /></button>
        <span style={{ fontSize: 15, fontWeight: 800, minWidth: 150, textAlign: 'center' }}>{monthLabel(month)}</span>
        <button className="icon-btn" onClick={() => setMonth((m) => shiftMonth(m, 1))} disabled={month >= currentMonth()} style={{ opacity: month >= currentMonth() ? 0.4 : 1 }}>
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 22, flexWrap: 'wrap' }}>
        <Stat label="Attendance" value={att?.percentage != null ? `${att.percentage}%` : '—'} />
        <Stat label="Present" value={att?.present ?? 0} color="var(--success)" />
        <Stat label="Absent" value={att?.absent ?? 0} color="var(--danger)" />
        <Stat label="Late" value={att?.late ?? 0} color="var(--warning)" />
        <Stat label="Marked days" value={att?.total_marked ?? 0} />
      </div>

      {/* Calendar */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--slate)' }} />
        </div>
      ) : !att || att.total_marked === 0 ? (
        <div className="empty">
          <div className="empty-art"><CalendarCheck size={38} /></div>
          <h3>No attendance marked</h3>
          <p>No records for {monthLabel(month)}.</p>
        </div>
      ) : (
        <div className="card card-pad">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-3)', paddingBottom: 4 }}>{d}</div>
            ))}
            {Array.from({ length: firstWeekday }).map((_, i) => <div key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const status = byDay[day];
              const c = status ? STATUS_COLOR[status] : null;
              return (
                <div key={day} title={status ? `${day}: ${status}` : String(day)}
                  style={{
                    aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 8, fontSize: 12.5, fontWeight: 600,
                    background: c ? c.bg : 'var(--surface-tint)',
                    color: c ? c.fg : 'var(--text-3)',
                    border: '1px solid var(--border)',
                  }}>
                  {day}
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, marginTop: 16, fontSize: 12, color: 'var(--text-2)' }}>
            <Legend color="var(--success)" label="Present" />
            <Legend color="var(--danger)" label="Absent" />
            <Legend color="var(--warning)" label="Late" />
          </div>
        </div>
      )}
    </div>
  );
};

function Stat({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.8px', color: color || 'var(--text)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: color }} />{label}
    </span>
  );
}

export default AttendanceTab;
